// editor/components/deck-build-shield.tsx
//
// NOTHING IN THE EDITOR TAKES A CLICK WHILE THE DECK IS BEING WRITTEN.
//
// A deck built with AI streams into the open editor slide by slide, and the
// runner places each one by position (how-to-start/deck-build-runner). The
// lock used to be an allow-list -- the canvas and the rail went pointer-dead,
// undo and add-slide were disabled, Present and the thumbnails stayed live --
// and every control it did not name was a way to break the build. So this is
// the other way round: ONE shield over the whole page, and one exception, the
// banner's Stop.
//
// Two layers, because neither alone covers everything:
//
//   - `inert` on every top-level node of the page: the app root and every
//     portal beside it (the element toolbar, popovers, menus), including ones
//     mounted while the build runs. The browser then ignores them for clicks,
//     focus and keys: Tab cannot reach a header button, a text field the user
//     was typing into loses focus, popovers whose triggers went inert close.
//     The banner and this shield carry LIVE_ATTR and are left alone, so Stop
//     stays reachable -- by pointer and by keyboard.
//   - a transparent fixed layer over the viewport, just under the banner,
//     carrying the busy cursor so the whole page reads as working, not broken.
//
// Hovering a control under the shield says why it does nothing: a tooltip,
// "Available once your slides are ready". The browser leaves inert nodes out
// of elementsFromPoint, so the control under the pointer is found by its box.
//
// Scrolling still works: a wheel over the shield scrolls the document behind
// it, which is how the slides are followed as they arrive.
//
// THE PLACEHOLDER SLIDE. The story opens with one blank slide the runner drops
// as soon as a real one lands. Its template copy -- "Presentation title", the
// empty text boxes -- looked like a slide to fill in, and the user filled it
// in. While it is a placeholder it is painted as a flat #D0CDD8 card that
// blinks slowly: something happening, nothing to type into. Scoped style, not
// a change to the renderer, keyed on the slide's `data-layer-id`, which is on
// its root in the canvas and on its thumbnail, so both go blank together.

import { useDeckBuildLock } from "@/pages/how-to-start/store/deck-build-lock";
import { useDeckBuildStore } from "@/pages/how-to-start/store/deck-build.store";
import { useAppSelector } from "@store";
import { getSlideIds } from "@store/story/selectors";
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

/** The flat card a placeholder slide shows while the deck builds. */
export const PLACEHOLDER_BACKGROUND = "#D0CDD8";

/**
 * Marks a top-level node that stays usable while the deck builds: the banner
 * with its Stop, and this shield's own layer. Everything else goes inert.
 */
export const LIVE_ATTR = "data-deck-build-live";

/** What the tooltip says over a control the shield is holding. */
export const LOCKED_ACTION_TOOLTIP = "Available once your slides are ready";

/** Hover this long on one control before the tooltip shows, like any tooltip. */
const TOOLTIP_DELAY_MS = 250;

/** What counts as a control worth explaining. Slide layers are not. */
const ACTION_SELECTOR = [
  "button",
  "a[href]",
  '[role="button"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="link"]',
  'input:not([type="hidden"])',
  "select",
  "textarea",
  "[data-thumbnail-id]",
].join(",");

/**
 * A box bigger than this share of the viewport is a wrapper, not a control:
 * a tooltip that follows the pointer across the whole canvas is noise.
 */
const MAX_TARGET_VIEWPORT_SHARE = 0.25;

/** How long the list of controls is reused before it is queried again. */
const TARGETS_TTL_MS = 500;

/** Tags that are never content and need no inert. */
const NOT_CONTENT = new Set([
  "SCRIPT",
  "STYLE",
  "LINK",
  "TEMPLATE",
  "NOSCRIPT",
]);

const NO_IDS: readonly string[] = [];

const escapeId = (id: string): string =>
  typeof globalThis.CSS?.escape === "function" ? CSS.escape(id) : id;

/**
 * The style that turns the given slides into blank, slowly blinking cards.
 * Empty when there is nothing to paint.
 *
 * Children go `opacity: 0` rather than `display: none` so the slide keeps its
 * height and the deck does not jump when the real slide replaces it. The
 * slide's own background (static/slide Background) is one of those children,
 * so the root's background is all that shows.
 *
 * Opacity, NOT visibility: `visibility: hidden` is inherited, and a descendant
 * that sets `visibility: visible` for itself shows straight through it -- the
 * text counter does (text/styles TextCounterContainer), and "0/700" floated
 * on the blank card. Nothing inside an opacity-0 box can make itself seen.
 */
export const placeholderSlideCss = (ids: readonly string[]): string => {
  if (!ids.length) return "";
  const roots = ids.map((id) => `[data-layer-id="${escapeId(id)}"]`).join(", ");
  const children = ids
    .map((id) => `[data-layer-id="${escapeId(id)}"] > *`)
    .join(", ");
  // The slide root paints its own ::before/::after (the active mat among
  // them), which no child selector reaches.
  const pseudos = ids
    .flatMap((id) => [
      `[data-layer-id="${escapeId(id)}"]::before`,
      `[data-layer-id="${escapeId(id)}"]::after`,
    ])
    .join(", ");
  return `
${roots} {
  background: ${PLACEHOLDER_BACKGROUND} !important;
  outline: none !important;
  box-shadow: none !important;
  animation: sd-deck-placeholder 2.4s ease-in-out infinite;
}
${children} {
  opacity: 0 !important;
}
${pseudos} {
  display: none !important;
}
@keyframes sd-deck-placeholder {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}
@media (prefers-reduced-motion: reduce) {
  ${roots} { animation: none; }
}`;
};

/**
 * Which slides are the placeholder(s) right now.
 *
 * The runner names them when the plan arrives (deck-build.store
 * placeholderIds). Before that -- while the deck is still being planned --
 * nothing real has been placed, so every slide in the deck is one. Once
 * something has been placed and the runner has named none (a retry), there
 * are none.
 */
const usePlaceholderSlideIds = (): readonly string[] => {
  const noted = useDeckBuildStore((s) => s.placeholderIds);
  const nothingPlaced = useDeckBuildStore(
    (s) => Object.keys(s.placedIds).length === 0,
  );
  const slideIds = useAppSelector(getSlideIds);
  if (noted.length) return noted;
  return nothingPlaced ? slideIds : NO_IDS;
};

/**
 * Makes every top-level node of the page inert while `active`, and keeps doing
 * so for nodes mounted meanwhile. Only the attribute this put on is taken off
 * again: a node that was inert for its own reasons stays that way.
 */
const useInertPage = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    const inerted = new Set<HTMLElement>();
    const hold = (node: Node) => {
      if (!(node instanceof HTMLElement)) return;
      if (NOT_CONTENT.has(node.tagName)) return;
      if (node.hasAttribute(LIVE_ATTR) || node.hasAttribute("inert")) return;
      node.setAttribute("inert", "");
      inerted.add(node);
    };
    for (const child of Array.from(document.body.children)) hold(child);

    // A popover, menu or toolbar can still mount mid-build; it goes inert too.
    const observer = new MutationObserver((records) => {
      for (const record of records) record.addedNodes.forEach(hold);
    });
    observer.observe(document.body, { childList: true });

    // Browsers blur a focused element that goes inert; done here as well so a
    // caret never stays in the placeholder's text while it blinks.
    const focused = document.activeElement;
    if (
      focused instanceof HTMLElement &&
      focused !== document.body &&
      !focused.closest(`[${LIVE_ATTR}]`)
    ) {
      focused.blur();
    }

    return () => {
      observer.disconnect();
      inerted.forEach((node) => node.removeAttribute("inert"));
    };
  }, [active]);
};

type TooltipAt = { left: number; top: number; above: boolean };

/** The control under (x, y), by its box -- inert nodes are not hit-tested. */
export const findLockedControlAt = (
  targets: readonly Element[],
  x: number,
  y: number,
  viewportArea: number,
): Element | null => {
  let best: Element | null = null;
  let bestArea = Number.POSITIVE_INFINITY;
  for (const el of targets) {
    if (el.closest(`[${LIVE_ATTR}]`)) continue;
    const rect = el.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (!area || area > viewportArea * MAX_TARGET_VIEWPORT_SHARE) continue;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      continue;
    }
    const visible =
      typeof el.checkVisibility !== "function" ||
      el.checkVisibility({ opacityProperty: true, visibilityProperty: true });
    if (!visible) continue;
    // The innermost control wins: an icon button inside a toolbar row.
    if (area < bestArea) {
      best = el;
      bestArea = area;
    }
  }
  return best;
};

/** Where the tooltip goes for a control: centred under it, or over it near the bottom. */
const tooltipPositionFor = (el: Element): TooltipAt => {
  const rect = el.getBoundingClientRect();
  const above = rect.bottom + 48 > window.innerHeight;
  // Clamped so a control at the edge of the screen keeps its tooltip on it.
  const left = Math.min(
    Math.max(rect.left + rect.width / 2, 130),
    window.innerWidth - 130,
  );
  return { left, top: above ? rect.top - 8 : rect.bottom + 8, above };
};

/** The tooltip over a held control: which control, and where to draw it. */
const useLockedControlTooltip = (active: boolean) => {
  const [at, setAt] = useState<TooltipAt | null>(null);
  const targetsRef = useRef<{ list: Element[]; at: number }>({
    list: [],
    at: 0,
  });
  const hoveredRef = useRef<Element | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameRef = useRef<number | null>(null);
  // Where the pointer is NOW: a frame reads the last move, not the first.
  const pointerRef = useRef({ x: 0, y: 0 });

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    hoveredRef.current = null;
    setAt(null);
  }, []);

  // Reset when the lock lifts (or the shield unmounts), so no hover timer
  // outlives the build.
  useEffect(() => {
    if (!active) return;
    return clear;
  }, [active, clear]);

  const targets = () => {
    const cached = targetsRef.current;
    const now = Date.now();
    if (now - cached.at < TARGETS_TTL_MS) return cached.list;
    // The editor's own controls, not the slides': a deck's buttons and links
    // -- every one again in its thumbnail -- run to hundreds, each box read
    // on every frame the pointer moves while the slides animate in.
    const list = Array.from(
      document.body.querySelectorAll(ACTION_SELECTOR),
    ).filter(
      (el) =>
        el.matches("[data-thumbnail-id]") || !el.closest("[data-layer-id]"),
    );
    targetsRef.current = { list, at: now };
    return list;
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointerRef.current = { x: event.clientX, y: event.clientY };
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const control = findLockedControlAt(
        targets(),
        pointerRef.current.x,
        pointerRef.current.y,
        window.innerWidth * window.innerHeight,
      );
      if (control === hoveredRef.current) return;
      hoveredRef.current = control;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      setAt(null);
      if (!control) return;
      timerRef.current = setTimeout(() => {
        if (hoveredRef.current === control) setAt(tooltipPositionFor(control));
      }, TOOLTIP_DELAY_MS);
    });
  };

  return { at, handlePointerMove, handlePointerLeave: clear };
};

const DeckBuildShield = () => {
  const isLocked = useDeckBuildLock();
  const placeholderIds = usePlaceholderSlideIds();
  useInertPage(isLocked);
  const { at, handlePointerMove, handlePointerLeave } =
    useLockedControlTooltip(isLocked);

  if (!isLocked) return null;

  return createPortal(
    <div {...{ [LIVE_ATTR]: "" }} data-testid="deck-build-shield-layer">
      <div
        aria-hidden
        data-testid="deck-build-shield"
        // Under the banner (z-[99999]) and over everything else, the header
        // (z-[9999]) and any dialog included.
        className="tw-fixed tw-inset-0 tw-z-[99998] tw-cursor-progress"
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onContextMenu={(event) => event.preventDefault()}
      />
      {at && (
        <span
          role="tooltip"
          data-testid="deck-build-locked-tooltip"
          style={{
            left: at.left,
            top: at.top,
            transform: `translate(-50%, ${at.above ? "-100%" : "0"})`,
          }}
          className="tw-pointer-events-none tw-fixed tw-z-[99999] tw-flex tw-w-max tw-max-w-[240px] tw-items-center tw-rounded-lg tw-bg-blue-base tw-px-3 tw-py-2 tw-font-sans tw-text-[13px]/tight tw-font-normal tw-text-white"
        >
          {LOCKED_ACTION_TOOLTIP}
        </span>
      )}
      {placeholderIds.length > 0 && (
        <style data-testid="deck-build-placeholder-style">
          {placeholderSlideCss(placeholderIds)}
        </style>
      )}
    </div>,
    document.body,
  );
};

export default DeckBuildShield;
